import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TaskService from "service/TaskService";
import { TaskFormSubmitReq, MoreAccessFormSubmitReq } from "domain/payload/request/task/TaskFormSubmitReq";
import {
  FieldTimeLog,
  FieldSummary,
  PageSummary,
  FormField,
  FormPage,
  SummaryPageParams,
} from "domain/model/SummaryPageParams";
import { MAPBOX_TOKEN } from "config/Mapbox";

interface ItemSummary {
  itemName: string;
  fields: FieldSummary[];
}

const SummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isDistanceTooFar, setIsDistanceTooFar] = useState(false);
  const [distanceValue, setDistanceValue] = useState<number>(0);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [isMapboxLoaded, setIsMapboxLoaded] = useState(false);

  const params = location.state as SummaryPageParams & {
    restoredCurrentPageId?: string;
    restoredPageStack?: string[];
    restoredServerStartTimestamp?: number;
    restoredDeviceTimeAtLoad?: number;
    formCode?: string;
    startLocation?: { lat: number; lng: number };
    taskForm?: Record<string, any>;
  };

  const isDemoMode = params?.isDemoMode || false;
  const isMoreAccessMode = params?.isMoreAccessMode || false;
  const moreAccessTId = params?.moreAccessTId;
  const moreAccessTaskId = params?.moreAccessTaskId;
  const moreAccessCoords = params?.moreAccessCoords;
  const photoMethods = params?.photoMethods || {};
  const fieldTimeLogsFromForm = params?.fieldTimeLogs || [];
  const summaryStartTime = params?.summaryStartTimestamp ?? Date.now();
  const startLocation = params?.startLocation || (moreAccessCoords ? { lat: moreAccessCoords.lat, lng: moreAccessCoords.lng } : null);
  const lockSubmitDistance: boolean = (params as any)?.lockSubmitDistance ?? true;
  const SUBMIT_DISTANCE_METERS = 100;

  const { sId, sessionData, formData, values, visitedPages, visitedFields, taskForm } = params || {};
  const rawKmlData = params?.kmlData || (location.state as any)?.kmlData || (formData as any)?.kmlData || taskForm?.kmlData || null;
  const [kmlData] = useState<string | null>(rawKmlData);

  const consentCode = params?.consentCode;
  const consentValue = params?.consentValue;
  const consentAttachmentUrl = params?.consentAttachmentUrl;

  const taskService = TaskService();
  const visitedFieldsSet = new Set(visitedFields || []);

  const parseKMLData = (kmlDataString: string) => {
    const extractedPolygons: any[] = [];
    const extractedPolylines: any[] = [];

    if (!kmlDataString || typeof kmlDataString !== "string" || !kmlDataString.trim()) {
      return { polygons: extractedPolygons, polylines: extractedPolylines };
    }

    try {
      let cleanData = kmlDataString.trim();
      if (cleanData.includes('\\"')) {
        cleanData = cleanData.replace(/\\"/g, '"');
      }

      const parsed = JSON.parse(cleanData);
      const items = Array.isArray(parsed) ? parsed : [parsed];

      items.forEach((item: any, index: number) => {
        if (!item) return;

        const outerCoords = item.outer || item.coordinates || item.polygon;
        if (Array.isArray(outerCoords) && outerCoords.length > 0) {
          const coordinates: [number, number][] = outerCoords
            .map((coord: any) => {
              if (Array.isArray(coord) && coord.length >= 2) {
                const lng = Number(coord[0]);
                const lat = Number(coord[1]);
                if (!isNaN(lng) && !isNaN(lat)) return [lng, lat] as [number, number];
              } else if (coord && typeof coord === "object") {
                const lng = Number(coord.lng ?? coord.longitude ?? coord.lon);
                const lat = Number(coord.lat ?? coord.latitude);
                if (!isNaN(lng) && !isNaN(lat)) return [lng, lat] as [number, number];
              }
              return null;
            })
            .filter((c): c is [number, number] => c !== null);

          if (coordinates.length >= 3) {
            const first = coordinates[0];
            const last = coordinates[coordinates.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
              coordinates.push([first[0], first[1]]);
            }

            extractedPolygons.push({
              coordinates,
              key: `polygon-json-${index}`,
            });
          }
        }

        const lineCoords = item.polyline || item.lineString || item.line;
        if (Array.isArray(lineCoords) && lineCoords.length > 0) {
          const coordinates: [number, number][] = lineCoords
            .map((coord: any) => {
              if (Array.isArray(coord) && coord.length >= 2) {
                const lng = Number(coord[0]);
                const lat = Number(coord[1]);
                if (!isNaN(lng) && !isNaN(lat)) return [lng, lat] as [number, number];
              } else if (coord && typeof coord === "object") {
                const lng = Number(coord.lng ?? coord.longitude ?? coord.lon);
                const lat = Number(coord.lat ?? coord.latitude);
                if (!isNaN(lng) && !isNaN(lat)) return [lng, lat] as [number, number];
              }
              return null;
            })
            .filter((c): c is [number, number] => c !== null);

          if (coordinates.length >= 2) {
            extractedPolylines.push({
              coordinates,
              key: `polyline-json-${index}`,
            });
          }
        }
      });

      if (extractedPolygons.length > 0 || extractedPolylines.length > 0) {
        return { polygons: extractedPolygons, polylines: extractedPolylines };
      }
    } catch (e) {
      // Fallthrough to XML parsing
    }

    try {
      const polygonRegex = /<Polygon>[\s\S]*?<\/Polygon>/gi;
      const polygonsMatch = kmlDataString.match(polygonRegex);

      if (polygonsMatch) {
        polygonsMatch.forEach((polygonStr, index) => {
          const coordsRegex = /<coordinates>([\s\S]*?)<\/coordinates>/i;
          const coordsMatch = polygonStr.match(coordsRegex);

          if (coordsMatch && coordsMatch[1]) {
            const coordsText = coordsMatch[1].trim();
            const points = coordsText.split(/\s+/).filter((p) => p.trim() !== "");

            const coordinates: [number, number][] = points
              .map((point) => {
                const parts = point.split(",").map(Number);
                if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                  return [parts[0], parts[1]] as [number, number];
                }
                return null;
              })
              .filter((c): c is [number, number] => c !== null);

            if (coordinates.length >= 3) {
              const first = coordinates[0];
              const last = coordinates[coordinates.length - 1];
              if (first[0] !== last[0] || first[1] !== last[1]) {
                coordinates.push([first[0], first[1]]);
              }
              extractedPolygons.push({
                coordinates,
                key: `polygon-xml-${index}`,
              });
            }
          }
        });
      }

      const lineStringRegex = /<LineString>[\s\S]*?<\/LineString>/gi;
      const linesMatch = kmlDataString.match(lineStringRegex);

      if (linesMatch) {
        linesMatch.forEach((lineStr, index) => {
          const coordsRegex = /<coordinates>([\s\S]*?)<\/coordinates>/i;
          const coordsMatch = lineStr.match(coordsRegex);

          if (coordsMatch && coordsMatch[1]) {
            const coordsText = coordsMatch[1].trim();
            const points = coordsText.split(/\s+/).filter((p) => p.trim() !== "");

            const coordinates: [number, number][] = points
              .map((point) => {
                const parts = point.split(",").map(Number);
                if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                  return [parts[0], parts[1]] as [number, number];
                }
                return null;
              })
              .filter((c): c is [number, number] => c !== null);

            if (coordinates.length >= 2) {
              extractedPolylines.push({
                coordinates,
                key: `polyline-xml-${index}`,
              });
            }
          }
        });
      }
    } catch (xmlErr) {
      console.error("Error parsing XML KML:", xmlErr);
    }

    return { polygons: extractedPolygons, polylines: extractedPolylines };
  };

  const getDistanceMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getStartMarkerSize = (distanceMeters: number) => {
    const baseSize = 24;
    const maxExtra = 5;
    const thresholdMeters = 40;
    if (distanceMeters >= thresholdMeters) return baseSize;
    const ratio = 1 - (distanceMeters / thresholdMeters);
    return Math.round(baseSize + maxExtra * ratio);
  };

  const startMarkerSize = getStartMarkerSize(distanceValue);

  useEffect(() => {
    if (!document.getElementById("mapbox-gl-css")) {
      const link = document.createElement("link");
      link.id = "mapbox-gl-css";
      link.rel = "stylesheet";
      link.href = "https://api.mapbox.com/mapbox-gl-js/v3.1.0/mapbox-gl.css";
      document.head.appendChild(link);
    }

    if ((window as any).mapboxgl) {
      setIsMapboxLoaded(true);
    } else {
      let script = document.getElementById("mapbox-gl-js") as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = "mapbox-gl-js";
        script.src = "https://api.mapbox.com/mapbox-gl-js/v3.1.0/mapbox-gl.js";
        script.onload = () => setIsMapboxLoaded(true);
        document.head.appendChild(script);
      } else {
        script.addEventListener("load", () => setIsMapboxLoaded(true));
      }
    }
  }, []);

  useEffect(() => {
    if (!isMapboxLoaded || !mapContainerRef.current) return;
    const mapboxgl = (window as any).mapboxgl;
    if (!mapboxgl) return;

    mapboxgl.accessToken = MAPBOX_TOKEN || process.env.REACT_APP_MAPBOX_TOKEN || "";

    const centerLat = startLocation && currentLocation
      ? (startLocation.lat + currentLocation.latitude) / 2
      : startLocation ? startLocation.lat : currentLocation ? currentLocation.latitude : 0;
    const centerLng = startLocation && currentLocation
      ? (startLocation.lng + currentLocation.longitude) / 2
      : startLocation ? startLocation.lng : currentLocation ? currentLocation.longitude : 0;

    if (mapInstanceRef.current) {
      try { mapInstanceRef.current.remove(); } catch (e) { }
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [centerLng, centerLat],
      zoom: 16,
      interactive: false,
    });

    mapInstanceRef.current = map;

    map.on("load", () => {
      if (startLocation) {
        const elStart = document.createElement("div");
        elStart.style.backgroundImage = "url(/assets/images/marker-blue.png)";
        elStart.style.width = `${startMarkerSize}px`;
        elStart.style.height = `${startMarkerSize}px`;
        elStart.style.backgroundSize = "contain";
        elStart.style.backgroundRepeat = "no-repeat";
        elStart.style.backgroundPosition = "center";

        new mapboxgl.Marker({ element: elStart })
          .setLngLat([startLocation.lng, startLocation.lat])
          .addTo(map);
      }

      if (currentLocation) {
        const elCurr = document.createElement("div");
        elCurr.style.backgroundImage = "url(/assets/images/marker-red.png)";
        elCurr.style.width = "24px";
        elCurr.style.height = "24px";
        elCurr.style.backgroundSize = "contain";
        elCurr.style.backgroundRepeat = "no-repeat";
        elCurr.style.backgroundPosition = "center";

        new mapboxgl.Marker({ element: elCurr })
          .setLngLat([currentLocation.longitude, currentLocation.latitude])
          .addTo(map);
      }

      if (startLocation && currentLocation) {
        map.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: [
                [startLocation.lng, startLocation.lat],
                [currentLocation.longitude, currentLocation.latitude],
              ],
            },
          },
        });

        map.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#94A3B8",
            "line-width": 2,
            "line-dasharray": [2, 2],
          },
        });
      }

      if (kmlData) {
        const { polygons: parsedPolygons, polylines: parsedPolylines } = parseKMLData(kmlData);

        if (parsedPolygons.length > 0) {
          map.addSource("kml-polygons", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: parsedPolygons.map((p: any) => ({
                type: "Feature",
                geometry: {
                  type: "Polygon",
                  coordinates: [p.coordinates],
                },
                properties: {},
              })),
            },
          });
          map.addLayer({
            id: "kml-polygons-fill",
            type: "fill",
            source: "kml-polygons",
            paint: {
              "fill-color": "#FF0000",
              "fill-opacity": 0.2,
            },
          });
          map.addLayer({
            id: "kml-polygons-outline",
            type: "line",
            source: "kml-polygons",
            paint: {
              "line-color": "#FF0000",
              "line-width": 2,
            },
          });
        }

        if (parsedPolylines.length > 0) {
          map.addSource("kml-polylines", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: parsedPolylines.map((p: any) => ({
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: p.coordinates,
                },
                properties: {},
              })),
            },
          });
          map.addLayer({
            id: "kml-polylines-layer",
            type: "line",
            source: "kml-polylines",
            paint: {
              "line-color": "#FF0000",
              "line-width": 3,
            },
          });
        }
      }

      const bounds = new mapboxgl.LngLatBounds();
      if (startLocation) bounds.extend([startLocation.lng, startLocation.lat]);
      if (currentLocation) bounds.extend([currentLocation.longitude, currentLocation.latitude]);

      if (kmlData) {
        const { polygons: parsedPolygons, polylines: parsedPolylines } = parseKMLData(kmlData);
        parsedPolygons.forEach((poly: any) => {
          poly.coordinates.forEach((pt: [number, number]) => bounds.extend(pt));
        });
        parsedPolylines.forEach((line: any) => {
          line.coordinates.forEach((pt: [number, number]) => bounds.extend(pt));
        });
      }

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 40, maxZoom: 17 });
      }
    });

    return () => {
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch (e) { }
        mapInstanceRef.current = null;
      }
    };
  }, [isMapboxLoaded, startLocation, currentLocation, startMarkerSize, kmlData]);

  const fetchCurrentLocationPreview = async () => {
    setIsLoadingLocation(true);
    if (!navigator.geolocation) {
      setIsLoadingLocation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setCurrentLocation(loc);
        if (startLocation) {
          const dist = getDistanceMeters(startLocation.lat, startLocation.lng, loc.latitude, loc.longitude);
          setDistanceValue(dist);
          setIsDistanceTooFar(lockSubmitDistance && dist >= SUBMIT_DISTANCE_METERS);
        }
        setIsLoadingLocation(false);
      },
      (err) => {
        console.warn("[SummaryPage] Gagal mengambil lokasi saat ini:", err);
        setIsLoadingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  useEffect(() => {
    if (!params) { navigate(-1); return; }
    console.log("[SummaryPage] Screen opened, summaryStartTimestamp:", summaryStartTime, "=", new Date(summaryStartTime).toISOString());
    fetchCurrentLocationPreview();
  }, []);

  const handleRestartTask = () => {
    if (moreAccessTId) {
      localStorage.removeItem(`webform_draft_maccess_${moreAccessTId}`);
    }
    if (moreAccessTaskId) {
      localStorage.removeItem(`webform_draft_task_${moreAccessTaskId}`);
    }
    if (sId) {
      localStorage.removeItem(`webform_draft_session_${sId}`);
    }
    if (formData?.id) {
      localStorage.removeItem(`webform_draft_task_${formData.id}`);
    }

    if (isMoreAccessMode) {
      const searchParams = new URLSearchParams();
      if (moreAccessTId) searchParams.set("tId", moreAccessTId);
      if (moreAccessTaskId) searchParams.set("taskId", moreAccessTaskId);
      navigate(`/mAccess?${searchParams.toString()}`);
    } else {
      handleBack();
    }
  };

  const getPageSummary = (): PageSummary[] => {
    if (!formData?.pages) return [];

    return (visitedPages || [])
      .map((pageId: string) => {
        const page = formData.pages?.find((p: FormPage) => p.id === pageId);
        if (!page) return null;

        const pageFields = (page.fields || [])
          .filter((field: FormField) => visitedFieldsSet.has(field.id))
          .map((field: FormField) => {
            const value = values[field.id];
            let displayValue: any = "";
            let isImage = false;

            if (!value) displayValue = "-";
            else if (field.type?.value === "Take Picture") { isImage = true; displayValue = value; }
            else if (field.type?.value === "Matrix Reference" || (field.type as any)?.key === 7) { displayValue = value; }
            else if (Array.isArray(value)) displayValue = value.join(", ");
            else displayValue = String(value);

            return { id: field.id, label: field.label, value: displayValue, type: field.type?.value, isImage };
          });

        return { pageName: page.name || "Form", fields: pageFields };
      })
      .filter(Boolean) as PageSummary[];
  };

  const getItemSummary = (): ItemSummary[] => {
    if (!formData?.items || isDemoMode) return [];
    return Object.entries(taskForm || {}).map(([itemId, itemValues]: [string, any]) => {
      const item = formData.items?.find((i: any) => i.id === itemId);
      const fields = item?.form?.pages?.[0]?.fields || [];

      const itemFields = fields.map((field: FormField) => {
        const value = itemValues[field.id];
        let displayValue: any = "";
        let isImage = false;

        if (!value) displayValue = "-";
        else if (field.type?.value === "Take Picture") {
          isImage = true;
          displayValue = value;
        } else if (field.type?.value === "Matrix Reference" || (field.type as any)?.key === 7) {
          displayValue = value;
        } else if (Array.isArray(value)) displayValue = value.join(", ");
        else displayValue = String(value);

        return {
          id: field.id,
          label: field.label,
          value: displayValue,
          type: field.type?.value,
          isImage,
        };
      });

      return {
        itemName: item?.name || "Item",
        fields: itemFields,
      };
    });
  };

  const handleBack = () => {
    const searchParams = new URLSearchParams();
    if (sId) searchParams.set("sId", sId);
    if (isDemoMode && params?.formCode) searchParams.set("formCode", params.formCode);

    const basePath = isDemoMode ? "/demo" : "/submission";

    navigate(`${basePath}?${searchParams.toString()}`, {
      state: {
        restoredValues: values,
        restoredCurrentPageId: params?.restoredCurrentPageId,
        restoredPageStack: params?.restoredPageStack ?? [],
        restoredVisitedPages: visitedPages,
        restoredVisitedFields: visitedFields,
        restoredFormData: formData,
        restoredSessionData: sessionData,
        restoredFieldTimeLogs: fieldTimeLogsFromForm,
        restoredServerStartTimestamp: params?.restoredServerStartTimestamp,
        restoredDeviceTimeAtLoad: params?.restoredDeviceTimeAtLoad,
        restoredMoreAccessTId: moreAccessTId,
        restoredMoreAccessTaskId: moreAccessTaskId,
        restoredMoreAccessCoords: moreAccessCoords,
        restoredPhotoMethods: photoMethods,
      },
    });
  };

  const handleSubmitClick = () => {
    if ((isMoreAccessMode || isDemoMode) && isDistanceTooFar && lockSubmitDistance) {
      alert(`Anda berada sekitar ${Math.round(distanceValue)} meter dari lokasi awal. Silakan mulai ulang task terlebih dahulu.`);
      return;
    }
    setShowConfirmModal(true);
  };

  const findSourceField = (targetFieldId: string) => {
    if (!formData?.pages) return null;
    const regex = new RegExp(`matrix_target\\(\\s*["']${targetFieldId}["']\\s*\\)`);
    for (const page of formData.pages) {
      if (page.fields) {
        for (const f of page.fields) {
          if (f.actionRules && regex.test(f.actionRules)) {
            return f;
          }
        }
      }
    }
    return null;
  };

  const formatFieldValue = (field: FormField) => {
    const rawValue = values[field.id];
    const isMatrix = field.type?.value === "Matrix Reference" || (field.type as any)?.key === 7;
    if (isMatrix) {
      const sourceField = findSourceField(field.id);
      const referenceId = sourceField ? sourceField.id : "";
      const subFields = rawValue && typeof rawValue === "object"
        ? Object.entries(rawValue).map(([lbl, val]) => ({
          label: lbl,
          value: Array.isArray(val) ? val.join(", ") : String(val || "")
        }))
        : [];
      return JSON.stringify({
        referenceId,
        fields: subFields,
      });
    }
    return Array.isArray(rawValue) ? rawValue.join(", ") : (rawValue || "");
  };

  const doSubmit = async () => {
    setShowConfirmModal(false);
    try {
      setIsSubmitting(true);

      if (isDemoMode) {
        navigate("/congrats?demo=true");
        return;
      }

      const allFields = (formData?.pages || []).flatMap((p: FormPage) => p.fields || []);

      const summaryLog: FieldTimeLog = {
        fieldId: "",
        endTimestamp: summaryStartTime,
        duration: 0,
        isSummary: true,
      };
      const allLogs = [...fieldTimeLogsFromForm, summaryLog];

      let submitCoords = currentLocation ? { lat: currentLocation.latitude, lng: currentLocation.longitude } : moreAccessCoords;
      if (!submitCoords && navigator.geolocation) {
        try {
          submitCoords = await new Promise<{ lat: number; lng: number }>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              (err) => reject(err),
              { enableHighAccuracy: true, timeout: 15000 }
            );
          });
        } catch (e) {
          console.warn("[SummaryPage] Gagal ambil lokasi terbaru:", e);
        }
      }

      if (isMoreAccessMode) {
        if (!moreAccessTId || !moreAccessTaskId) { alert("Sesi tidak valid"); return; }

        const moreAccessValues = allFields.map((field: FormField) => {
          const isTakePicture = field.type?.value === "Take Picture";

          let methodStr: string | null = null;
          if (isTakePicture) {
            const methods = photoMethods?.[field.id] || [];
            methodStr = methods.length > 0 ? methods.join(", ") : null;
          }

          return {
            fieldId: field.id,
            label: field.label,
            value: formatFieldValue(field),
            method: methodStr,
          };
        });

        const payload: MoreAccessFormSubmitReq = {
          taskId: moreAccessTaskId,
          values: moreAccessValues,
          latitude: submitCoords?.lat ?? 0,
          longitude: submitCoords?.lng ?? 0,
          startLatitude: startLocation?.lat ?? moreAccessCoords?.lat ?? 0,
          startLongitude: startLocation?.lng ?? moreAccessCoords?.lng ?? 0,
          startDate: sessionData?.startDate || null,
          sessionId: sessionData?.sessionId || undefined,
          fieldTimeLogs: allLogs,
          timezoneOffset: -new Date().getTimezoneOffset() / 60,
          ...(consentCode ? {
            consentCode,
            consentValue,
            consentAttachmentUrl,
          } : {}),
        };

        await taskService.taskSubmitDataByMAccess(moreAccessTId, payload);
        if (moreAccessTId) {
          localStorage.removeItem(`webform_draft_maccess_${moreAccessTId}`);
        }
        if (moreAccessTaskId) {
          localStorage.removeItem(`webform_draft_task_${moreAccessTaskId}`);
        }
        const targetTaskId = moreAccessTaskId || formData?.id || "";
        navigate(`/mAccess/congrats?moreAccess=true&taskId=${targetTaskId}`);
        return;
      }

      if (!sId) { alert("Invalid session ID"); return; }

      const mainValues = allFields.map((field: FormField) => ({
        fieldId: field.id,
        label: field.label,
        value: formatFieldValue(field),
      }));

      const payload: TaskFormSubmitReq = {
        webSessionId: sId,
        values: mainValues,
        resultSessionId: sessionData?.sessionId || null,
        startDate: sessionData?.startDate || null,
        latitude: submitCoords?.lat ?? 0.0,
        longitude: submitCoords?.lng ?? 0.0,
        fieldTimeLogs: allLogs,
        timezoneOffset: -new Date().getTimezoneOffset() / 60,
      };

      console.log("[SummaryPage] Submitting with summaryStartTime:", new Date(summaryStartTime).toISOString());

      await taskService.taskSubmitData(payload);
      if (sId) {
        localStorage.removeItem(`webform_draft_session_${sId}`);
      }
      if (formData?.id) {
        localStorage.removeItem(`webform_draft_task_${formData.id}`);
      }
      navigate(`/congrats?sId=${sId}`);
    } catch (err: any) {
      console.error("[SummaryPage] Submit error:", err);
      alert(err?.response?.data?.message || err?.message || "Gagal submit form");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldValue = (field: FieldSummary) => {
    const isMatrix = field.type === "Matrix Reference" || field.type === "7";
    if (isMatrix) {
      const rawValue = field.value;
      if (!rawValue || rawValue === "-" || typeof rawValue !== "object") {
        return <span className="small text-secondary">-</span>;
      }
      return (
        <div className="ps-2 mt-1 border-start border-2 border-secondary-subtle">
          {Object.entries(rawValue).map(([subLabel, subVal]: [string, any], idx) => {
            const isSubImage = Array.isArray(subVal) || (typeof subVal === "string" && (subVal.startsWith("http") || subVal.startsWith("file") || subVal.startsWith("data:")));
            return (
              <div key={idx} className="mb-2">
                <div className="fw-semibold small text-muted">{subLabel}</div>
                {isSubImage ? (
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {(Array.isArray(subVal) ? subVal : [subVal]).map((uri: string, imgIdx: number) => (
                      <div key={imgIdx} style={{ width: 80, height: 80, borderRadius: 6, overflow: "hidden", backgroundColor: "#eee" }}>
                        <img src={uri} alt="matrix-thumb" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="small text-dark">{Array.isArray(subVal) ? subVal.join(", ") : String(subVal || "-")}</span>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    if (field.isImage && field.value && field.value !== "-") {
      const imageUris: string[] = Array.isArray(field.value) ? field.value : [field.value];
      if (imageUris.length === 0) return <span className="small text-secondary">-</span>;
      return (
        <div className="d-flex flex-wrap gap-2 mt-1">
          {imageUris.map((uri: string, idx: number) => (
            <div key={idx} style={{ width: 100, height: 100, borderRadius: 8, overflow: "hidden", backgroundColor: "#eee" }}>
              <img src={uri} alt={`photo-${idx}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ))}
        </div>
      );
    }
    return <span className="small" style={{ color: "#333" }}>{field.value}</span>;
  };

  const pageSummary = getPageSummary();
  const itemSummary = getItemSummary();

  if (!params) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-muted">Data tidak ditemukan.</div>
      </div>
    );
  }

  const startLat = startLocation?.lat;
  const startLng = startLocation?.lng;
  const currLat = currentLocation?.latitude;
  const currLng = currentLocation?.longitude;

  const minLat = Math.min(startLat ?? currLat ?? 0, currLat ?? startLat ?? 0) - 0.002;
  const maxLat = Math.max(startLat ?? currLat ?? 0, currLat ?? startLat ?? 0) + 0.002;
  const minLng = Math.min(startLng ?? currLng ?? 0, currLng ?? startLng ?? 0) - 0.002;
  const maxLng = Math.max(startLng ?? currLng ?? 0, currLng ?? startLng ?? 0) + 0.002;

  const mapEmbedUrl = (currLat && currLng)
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${minLng},${minLat},${maxLng},${maxLat}&layer=mapnik&marker=${currLat},${currLng}`
    : null;

  return (
    <div className="container py-4" style={{ paddingBottom: 100 }}>
      {isDemoMode && (
        <div className="alert alert-info mb-3 py-2 small" style={{ borderRadius: "8px" }}>
          Ini adalah mode uji coba. Data yang dimasukkan tidak akan disimpan.
        </div>
      )}

      {isMoreAccessMode && (
        <div className="alert alert-warning mb-3 py-2 small" style={{ borderRadius: "8px" }}>
          Anda sedang mengerjakan tugas via Web Form. Pastikan data di bawah sudah benar sebelum mengirim.
        </div>
      )}

      {(isMoreAccessMode || isDemoMode) && (startLocation || currentLocation) && (
        <div className="bg-white p-3 mb-3 border rounded">
          <div className="fw-semibold mb-2" style={{ color: "#2B75DA", fontSize: 15 }}>
            Lokasi
          </div>

          <div
            ref={mapContainerRef}
            className="position-relative overflow-hidden rounded mb-2"
            style={{ height: 220, backgroundColor: "#e9ecef" }}
          >
            {!isMapboxLoaded && !mapEmbedUrl && (
              <div className="d-flex justify-content-center align-items-center h-100 text-muted small">
                {isLoadingLocation ? "Memuat peta lokasi..." : "Lokasi tidak dapat ditampilkan"}
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-3 mb-2 small">
            <div className="d-flex align-items-center gap-1">
              <img src="/assets/images/marker-blue.png" alt="Lokasi Mulai" style={{ width: 16, height: 16, objectFit: "contain" }} />
              <span className="text-secondary">Lokasi Mulai</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <img src="/assets/images/marker-red.png" alt="Lokasi Saat Ini" style={{ width: 16, height: 16, objectFit: "contain" }} />
              <span className="text-secondary">
                Lokasi Saat Ini {isLoadingLocation && "(memuat...)"}
              </span>
            </div>
          </div>

          {startLocation && currentLocation && (
            <div className={`small fw-semibold mb-2 ${isDistanceTooFar ? "text-danger" : "text-secondary"}`}>
              Jarak dari titik mulai: {Math.round(distanceValue)} meter
            </div>
          )}

          {isDistanceTooFar && !isDemoMode && (
            <div className="alert alert-danger p-2 small mb-2 d-flex align-items-center justify-content-between">
              <span>Lokasi Anda terlalu jauh (≥ 100m) dari titik awal task.</span>
              <button className="btn btn-danger btn-sm text-nowrap ms-2" onClick={handleRestartTask}>
                Mulai Ulang Task
              </button>
            </div>
          )}

          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
            onClick={fetchCurrentLocationPreview}
            disabled={isLoadingLocation}
          >
            {isLoadingLocation ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              "Refresh Lokasi Saat Ini"
            )}
          </button>
        </div>
      )}

      {pageSummary.map((page: PageSummary, index: number) => (
        <div
          key={index}
          className="bg-white p-3 mb-3"
          style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}
        >
          <div className="fw-semibold mb-3" style={{ color: "#2B75DA", fontSize: 15 }}>
            {page.pageName}
          </div>
          {page.fields.map((field: FieldSummary, fieldIndex: number) => (
            <div key={fieldIndex} className="mb-2">
              <div className="fw-semibold small mb-1" style={{ color: "#000" }}>
                {field.label}
              </div>
              {renderFieldValue(field)}
            </div>
          ))}
        </div>
      ))}

      {itemSummary.map((item: ItemSummary, index: number) => (
        <div
          key={`item-${index}`}
          className="bg-white p-3 mb-3"
          style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}
        >
          <div className="fw-semibold mb-3" style={{ color: "#10B981", fontSize: 15 }}>
            {item.itemName}
          </div>
          {item.fields.map((field: FieldSummary, fieldIndex: number) => (
            <div key={fieldIndex} className="mb-2">
              <div className="fw-semibold small mb-1" style={{ color: "#000" }}>
                {field.label}
              </div>
              {renderFieldValue(field)}
            </div>
          ))}
        </div>
      ))}

      <div
        className="border-top p-3 d-flex justify-content-between align-items-center"
        style={{ backgroundColor: "#fff" }}
      >
        <button
          className="btn btn-light d-flex align-items-center gap-2"
          onClick={handleBack}
          style={{ border: "1px solid #dee2e6" }}
          disabled={isSubmitting}
        >
          <span style={{ fontSize: "18px" }}>←</span>
          Kembali
        </button>

        <button
          className="btn btn-primary d-flex align-items-center gap-2 px-4"
          disabled={isSubmitting}
          style={{ minHeight: "44px", borderRadius: "999px" }}
          onClick={handleSubmitClick}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" />
              Submitting...
            </>
          ) : isDemoMode ? (
            <>
              Selesai
              <span style={{ fontSize: "18px" }}>✓</span>
            </>
          ) : (
            <>
              Kirim
              <span style={{ fontSize: "18px" }}>✓</span>
            </>
          )}
        </button>
      </div>

      {showConfirmModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => { if (!isSubmitting) setShowConfirmModal(false); }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <div className="modal-header border-bottom py-3">
                <h6 className="modal-title fw-semibold mb-0">
                  {isDemoMode ? "Konfirmasi Selesai" : "Konfirmasi Submit"}
                </h6>
              </div>
              <div className="modal-body py-4">
                <p className="mb-0 small" style={{ color: "#444" }}>
                  {isDemoMode
                    ? "Apakah Anda yakin ingin menyelesaikan demo form ini?"
                    : "Apakah Anda yakin ingin mengirim data ini?"}
                </p>
              </div>
              <div className="modal-footer border-top d-flex gap-2 py-3">
                <button
                  className="btn btn-light flex-grow-1"
                  style={{ border: "1px solid #dee2e6" }}
                  onClick={() => setShowConfirmModal(false)}
                >
                  Batal
                </button>
                <button
                  className="btn btn-primary flex-grow-1"
                  style={{ borderRadius: "8px" }}
                  onClick={doSubmit}
                >
                  {isDemoMode ? "Selesai" : "Ya, Kirim"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryPage;